import * as React from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbItem } from '@ministry/ui/internal';

/**
 * ONE page-header pattern, used by every internal-portal screen that isn't a single-record
 * detail view (File Detail keeps its own identity-card header — tracking code + status +
 * subject is a different kind of header, not a list/workspace title). Named directly in
 * review as a defect before this existed: the dashboard stacked three left-aligned lines
 * (title, "स्वागत छ, <name>", a raw role code) before any real content, and File Register's
 * title sat naked above a heavy filter card with no visual relationship between the two.
 *
 * Structure is deliberately plain: an optional `Breadcrumb` above everything else, an
 * `h1`, an optional single muted description/subtitle line below it (never three), and
 * an optional trailing `actions` slot on the same row as the title so primary actions
 * (e.g. a future "New file" button) don't need their own row. A single bottom border
 * gives every page the same amount of separation from its content, which is the "one
 * spacing scale, actually applied" rule applied to page chrome.
 *
 * **Named defect this pass fixes**: "no proper back navigation in nested views —
 * breadcrumbs exist on File Detail only." Appointment Detail had already grown its own
 * ad-hoc inline `Breadcrumb` (a real but non-reusable fix); Entrance Verification,
 * Account Settings, File Intake and the Notification Centre had no equivalent at all —
 * a user landing on any of those from a deep link, or three clicks in, had only the
 * browser's own back button (CLAUDE.md's brief: "not an answer"). `breadcrumb` is one
 * optional prop, using this package's OWN `Breadcrumb` (`@ministry/ui`) wired to
 * `react-router`'s `Link` exactly once, here, so every screen that adopts it gets the
 * identical behavior (client-side navigation, `aria-current="page"` on the leaf, no
 * full-page reload) rather than each screen re-wiring the same three lines.
 */
export interface PageHeaderProps {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  /** Root-first order; the final item is rendered as the non-link current page. Omit entirely for a page reached only from the primary nav (its own sidebar entry already answers "where am I"). */
  breadcrumb?: BreadcrumbItem[];
}

export function PageHeader({ title, description, actions, breadcrumb }: PageHeaderProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 border-b border-border-decorative pb-4">
      {breadcrumb ? (
        <Breadcrumb
          items={breadcrumb}
          renderLink={(item) => (
            <Link to={item.href as string} className="text-text-link no-underline hover:underline">
              {item.label}
            </Link>
          )}
        />
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-text-heading">{title}</h1>
          {description ? <div className="text-sm text-text-muted">{description}</div> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
